import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { catchError, concatMap, filter, from, groupBy, map, mergeMap, of, startWith, switchMap, take } from 'rxjs';

import { MachineDoc } from '@models';
import { MachineDocService } from '../../services/machine-doc.service';
import { DocsActions } from './docs.actions';
import { selectDoc, selectDocStatus } from './docs.selectors';
import { docKey, normalizeMachineDoc, pickHighestVersion } from './docs.normalize';

function isEmptyData(d: MachineDoc | null): boolean {
  const data = d?.data;
  return !data || typeof data !== 'object' || Array.isArray(data) || Object.keys(data).length === 0;
}

async function loadOne(svc: MachineDocService, projectId: string, key: string): Promise<MachineDoc | null> {
  const res = await svc.find({ where: { projectId, key } as any });
  const docs = ((res.data ?? []) as MachineDoc[]).map(normalizeMachineDoc);
  let doc = pickHighestVersion(docs);
  // One-shot hydration only if a list query returned an empty payload.
  if (doc?.id && isEmptyData(doc)) {
    try { doc = normalizeMachineDoc(await svc.findById(doc.id)); } catch { /* keep doc */ }
  }
  return doc;
}

async function saveOne(svc: MachineDocService, projectId: string, key: string, data: any, existing: MachineDoc | null): Promise<MachineDoc> {
  // A `local:`-prefixed id is a synthetic placeholder created by an optimistic
  // applyLocalData write — there is no server record yet, so save as a create
  // (avoids a guaranteed-failing update round-trip on the first save).
  const hasRealId = !!existing?.id && !String(existing.id).startsWith('local:');
  const doc = hasRealId ? normalizeMachineDoc(existing as MachineDoc) : new MachineDoc();
  doc.projectId = projectId;
  doc.key = key;
  doc.data = data;
  doc.version = (hasRealId ? existing?.version ?? 0 : 0) + 1;
  try {
    return normalizeMachineDoc(await svc.save(doc));
  } catch (e: any) {
    const msg = String(e?.message ?? '');
    const staleId = !!doc.id && (msg.includes('Invalid Request') || msg.includes('Bad Request') || e?.status === 400 || e?.status === 404);
    if (!staleId) throw e;
    const fresh = new MachineDoc();
    fresh.projectId = projectId; fresh.key = key; fresh.data = data; fresh.version = 1;
    return normalizeMachineDoc(await svc.save(fresh));
  }
}

/** Load a single (projectId,key) doc once. */
export const loadDoc$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), svc = inject(MachineDocService)) =>
    actions$.pipe(
      ofType(DocsActions.loadDoc),
      concatLatestFrom(({ projectId, key }) => store.select(selectDocStatus(projectId, key))),
      filter(([, status]) => status === 'idle' || status === 'error'),
      mergeMap(([{ projectId, key }]) =>
        from(loadOne(svc, projectId, key)).pipe(
          map((doc) => DocsActions.loadDocSucceeded({ projectId, key, doc })),
          catchError((err) => of(DocsActions.loadDocFailed({ projectId, key, error: String(err?.message ?? err) }))),
          startWith(DocsActions.loadDocStarted({ projectId, key }))
        )
      )
    ),
  { functional: true }
);

/**
 * Write-through save with create-on-stale-id retry.
 * Saves are serialized PER (projectId,key) via groupBy+concatMap, and the
 * current cached doc is read at PROCESSING time (take(1), inside the group) —
 * not at action-arrival time. This guarantees a second rapid save to the same
 * key sees the real server id repointed by the first save's saveDocSucceeded,
 * so it updates instead of creating a duplicate record. Different keys still
 * save concurrently (mergeMap across groups).
 */
const saveDoc$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), svc = inject(MachineDocService)) =>
    actions$.pipe(
      ofType(DocsActions.saveDoc),
      groupBy(({ projectId, key }) => docKey(projectId, key)),
      mergeMap((group$) =>
        group$.pipe(
          concatMap(({ projectId, key, data }) =>
            store.select(selectDoc(projectId, key)).pipe(
              take(1),
              switchMap((existing) =>
                from(saveOne(svc, projectId, key, data, existing ?? null)).pipe(
                  map((doc) => DocsActions.saveDocSucceeded({ projectId, key, doc })),
                  catchError((err) => of(DocsActions.saveDocFailed({ projectId, key, error: String(err?.message ?? err) })))
                )
              )
            )
          )
        )
      )
    ),
  { functional: true }
);

export const docsEffects = { loadDoc$, saveDoc$ };
